/**
 * ColourMatch Pro · Presentations — cross-project index.
 *
 * There is no cross-project presentations endpoint today, so we build
 * one from `GET /api/pro/cms/projects` by fetching the presentation for
 * each recent project. We only surface projects that already have a
 * created presentation — no fake entries.
 *
 * If none exist, we present a real, actionable empty state that guides
 * the painter into a project (where presentations are created).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Presentation as PresentationIcon,
  Plus,
  Loader2,
  ArrowRight,
  Send,
  Eye,
  FolderKanban,
} from "lucide-react";

export default function ProPresentationsList() {
  const [state, setState] = useState({ loading: true, entries: [], hasProjects: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const projectsRes = await api.get("/pro/cms/projects?page_size=25");
        const projects = projectsRes.data?.items || [];
        if (!alive) return;
        // Fetch presentations for each project in parallel. Missing ones (404)
        // just resolve as null and are filtered out — this is deliberate and
        // never surfaces a fake entry.
        const perProject = await Promise.all(
          projects.map((p) =>
            api.get(`/pro/cms/consultations/${p.id}/presentation`)
              .then((r) => ({ project: p, presentation: r.data }))
              .catch(() => null)
          )
        );
        if (!alive) return;
        const entries = perProject
          .filter(Boolean)
          .sort((a, b) => new Date(b.presentation.updated_at || b.presentation.created_at) - new Date(a.presentation.updated_at || a.presentation.created_at));
        setState({ loading: false, entries, hasProjects: projects.length > 0 });
      } catch {
        if (alive) setState({ loading: false, entries: [], hasProjects: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  const statusStyles = {
    draft:      { bg: "bg-brand-linen/60", fg: "text-brand-ink",     label: "Draft" },
    ready:      { bg: "bg-blue-50",        fg: "text-blue-700",       label: "Ready" },
    sent:       { bg: "bg-brand-teal/10",  fg: "text-brand-teal",     label: "Sent" },
    viewed:     { bg: "bg-emerald-50",     fg: "text-emerald-700",    label: "Viewed" },
    accepted:   { bg: "bg-emerald-100",    fg: "text-emerald-800",    label: "Accepted" },
    superseded: { bg: "bg-amber-50",       fg: "text-amber-800",      label: "Superseded" },
    revoked:    { bg: "bg-brand-ink/10",   fg: "text-brand-ink/60",   label: "Revoked" },
  };

  return (
    <div className="space-y-8" data-testid="pro-presentations-list">
      <header>
        <div className="overline text-brand-teal mb-2">ColourMatch Pro</div>
        <h1 className="font-serif text-3xl text-brand-ink leading-tight">Visualisation Proposals</h1>
        <p className="text-sm text-brand-muted mt-2 max-w-2xl leading-relaxed">
          Client-facing visualisation proposals — cover page, existing property photos, rendered concepts, colour schedule, scope of works, investment and acceptance. Proposals are created inside a client project.
        </p>
      </header>

      {state.loading ? (
        <div className="border border-brand-linen bg-white rounded-2xl p-8 flex items-center gap-3 text-brand-muted text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading presentations…
        </div>
      ) : state.entries.length === 0 ? (
        <div className="border border-dashed border-brand-linen bg-white rounded-2xl p-10 lg:p-14 text-center" data-testid="presentations-empty">
          <div className="w-12 h-12 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-4">
            <PresentationIcon className="w-5 h-5" />
          </div>
          <div className="font-serif text-xl text-brand-ink mb-2">
            {state.hasProjects ? "No proposals yet" : "Start a client project first"}
          </div>
          <p className="text-sm text-brand-muted max-w-md mx-auto mb-6 leading-relaxed">
            {state.hasProjects
              ? "Open a client project and build a professional visualisation proposal from your rendered concepts."
              : "Visualisation proposals are created inside client projects. Start a client project to prepare your first proposal."}
          </p>
          <Link
            to={state.hasProjects ? "/pro/projects" : "/pro/projects?new=1"}
            className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-teal transition-colors"
            data-testid="presentations-empty-cta"
          >
            {state.hasProjects
              ? <><FolderKanban className="w-3.5 h-3.5" /> Open Projects</>
              : <><Plus className="w-3.5 h-3.5" /> New client project</>}
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-brand-linen rounded-2xl border border-brand-linen bg-white overflow-hidden" data-testid="presentations-rows">
          {state.entries.map(({ project, presentation }) => {
            const s = statusStyles[presentation.status] || statusStyles.draft;
            const views = presentation.view_count || 0;
            return (
              <li key={presentation.id}>
                <Link
                  to={`/pro/projects/${project.id}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-brand-linen/20 transition-colors"
                  data-testid={`presentation-row-${presentation.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0">
                    <PresentationIcon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-brand-ink truncate">
                      {presentation.title || project.name}
                    </div>
                    <div className="text-xs text-brand-muted mt-0.5 truncate">
                      {project.client_name || "—"} · {project.name}
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 text-xs text-brand-muted flex-shrink-0">
                    {presentation.share_url && (
                      <span className="inline-flex items-center gap-1"><Send className="w-3 h-3" /> Shared</span>
                    )}
                    {views > 0 && (
                      <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {views}</span>
                    )}
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold ${s.bg} ${s.fg}`}>
                    {s.label}
                  </span>
                  <ArrowRight className="w-4 h-4 text-brand-muted flex-shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
